import { useSwipeable } from 'react-swipeable';
import { haptics } from '@/shared/lib/telegram';

/**
 * Ширина полосы совпадает с боковым отступом страниц (`px-4`/`px-5` — см.
 * PageLayout/ChatScreen): жест не наползает на реальные кнопки в шапках,
 * которые начинаются сразу за этим отступом.
 */
const EDGE_ZONE_PX = 16;
/** Порог свайпа — как у `SwipeCard`, чтобы не сработать от дрожания пальца. */
const THRESHOLD_PX = 80;

interface SwipeBackZoneProps {
  onBack: () => void;
}

/**
 * Невидимая полоса у левого края экрана — свайп вправо с неё уводит назад,
 * как в нативных мобильных приложениях (дублирует `useTelegramBackButton`
 * там, где у устройства/клиента нет своего системного жеста).
 *
 * Раньше жест ловили голыми `touchstart/move/end` на `document` — не
 * работало: без `preventDefault` в `touchmove` системный edge-свайп
 * (WebView/браузер) перехватывает событие себе, и до `touchend` дело не
 * доходит. `react-swipeable` с `preventScrollOnSwipe` слушает `touchmove`
 * не пассивно и сам вызывает `preventDefault`, удерживая жест за собой.
 *
 * Отдельный DOM-узел у края, а не слушатель на `document`: у самого
 * `useSwipeable` нет штатного способа отписаться от произвольного элемента
 * (`ref(null)` в библиотеке — no-op), поэтому монтирование/размонтирование
 * этого узла вместе со страницей — единственный чистый способ снять
 * листенеры при уходе с неё.
 */
export function SwipeBackZone({ onBack }: SwipeBackZoneProps) {
  const handlers = useSwipeable({
    delta: THRESHOLD_PX,
    preventScrollOnSwipe: true,
    onSwipedRight: () => {
      haptics.advance();
      onBack();
    },
  });

  return (
    <div
      {...handlers}
      aria-hidden="true"
      className="fixed inset-y-0 left-0 z-40"
      style={{ width: EDGE_ZONE_PX, touchAction: 'pan-y' }}
    />
  );
}
